/**
 * Pure search + amount-parsing helpers for DocumentList, extracted from the
 * component.
 *
 * `filterDocuments` matches a document when the (lowercased) query is a
 * substring of its number, description, status, OR formatted total amount; an
 * empty query matches everything. It is the SINGLE source of truth for that
 * filter: the list previously ran one amount-aware copy for rendering while the
 * page-auto-adjust effect ran a SEPARATE copy that omitted the amount, so
 * searching by amount mis-counted the pages. Both call this now.
 *
 * No money is rendered here — `total_amount` only feeds the search match — so
 * this is wall-neutral. Generic over the document shape so it returns the
 * caller's own item type.
 */

/** The fields `filterDocuments` reads off each document. */
export interface DocumentSearchFields {
  number?: string | null
  description?: string | null
  status?: string | null
  total_amount: string | number
}

/** Parse the CRM's string-or-number money into a number (blank/NaN → 0). */
export function parseDocumentAmount(amount: string | number): number {
  if (typeof amount === "string") {
    return parseFloat(amount) || 0
  }
  return amount || 0
}

/** Documents whose number / description / status / amount contains `searchQuery` (case-insensitive). */
export function filterDocuments<D extends DocumentSearchFields>(
  documents: D[],
  searchQuery: string,
): D[] {
  return documents.filter((doc) => {
    if (!searchQuery) return true
    const searchLower = searchQuery.toLowerCase()
    return (
      doc.number?.toLowerCase().includes(searchLower) ||
      doc.description?.toLowerCase().includes(searchLower) ||
      doc.status?.toLowerCase().includes(searchLower) ||
      parseDocumentAmount(doc.total_amount).toString().includes(searchLower)
    )
  })
}
